import { useState, useEffect, useCallback } from 'react';
import { TourData, TourStorage, TourEvent } from '../components/OnboardingTour/types';
import { getNextTour } from '../components/OnboardingTour/tourConfigs';

const STORAGE_KEY = 'oogwai_onboarding_tours';

// Default storage structure
const defaultStorage: TourStorage = {
  completedTours: [],
  skippedTours: [],
  lastShown: {},
  viewCount: {},
};

// LocalStorage utilities
const getTourStorage = (): TourStorage => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? { ...defaultStorage, ...JSON.parse(data) } : defaultStorage;
  } catch (error) {
    console.warn('Failed to parse onboarding tour storage:', error);
    return defaultStorage;
  }
};

const saveTourStorage = (data: TourStorage): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save onboarding tour storage:', error);
  }
};

// Analytics tracking (optional - replace with your analytics service)
const trackTourEvent = (event: TourEvent, tourId: string, step?: number): void => {
  // Replace with your analytics tracking
  console.log('Tour Event:', { event, tourId, step, timestamp: new Date().toISOString() });
  
  // Example integration with common analytics services:
  // gtag('event', 'onboarding_tour', { event_category: event, event_label: tourId, value: step });
  // analytics.track('Onboarding Tour', { event, tourId, step });
};

interface UseOnboardingTourOptions {
  /**
   * Delay in milliseconds before showing the tour
   * @default 1000
   */
  delay?: number;

  /**
   * Whether to show the tour even if it was skipped before
   * @default false
   */
  showIfSkipped?: boolean;

  /**
   * Maximum number of times to show the tour
   * @default 3
   */
  maxViewCount?: number;

  /**
   * Whether to automatically start the tour on first visit
   * @default true
   */
  autoStart?: boolean;

  /**
   * Whether to automatically chain to the next tour in sequence when this tour completes
   * @default false
   */
  chainToNext?: boolean;
}

interface UseOnboardingTourReturn {
  /** Whether the tour should be running */
  runTour: boolean;

  /** Current tour data */
  currentTour: TourData | null;

  /** Start a specific tour */
  startTour: (tour: TourData, isChainedTour?: boolean) => void;

  /** Close/complete the current tour */
  closeTour: () => void;

  /** Skip the current tour */
  skipTour: () => void;

  /** Reset all tour data (for development/testing) */
  resetTours: () => void;

  /** Check if a specific tour has been completed */
  isTourCompleted: (tourId: string) => boolean;

  /** Check if a specific tour has been skipped */
  isTourSkipped: (tourId: string) => boolean;

  /** Get tour statistics */
  getTourStats: () => TourStorage;
}

export const useOnboardingTour = (
  tourToShow: TourData | null,
  options: UseOnboardingTourOptions = {}
): UseOnboardingTourReturn => {
  const {
    delay = 1000,
    showIfSkipped = false,
    maxViewCount = 3,
    autoStart = true,
    chainToNext = false,
  } = options;

  const [runTour, setRunTour] = useState(false);
  const [currentTour, setCurrentTour] = useState<TourData | null>(null);
  const [isInChainSequence, setIsInChainSequence] = useState(false);

  // Clean up stale active tour ID on mount
  useEffect(() => {
    const activeTourId = localStorage.getItem('activeTourId');
    if (activeTourId) {
      localStorage.removeItem('activeTourId');
    }
  }, []);

  // Check if tour should be shown
  const shouldShowTour = useCallback((tour: TourData, isChainedTour: boolean = false): boolean => {
    if (!tour) {
      return false;
    }

    // Disable tour if running inside an iframe
    if (window.self !== window.top) {
      return false;
    }

    // Check if we're on the required page for this tour
    // Use startsWith to allow for nested routes (e.g., /company-settings/general matches /company-settings)
    if (tour.requiredPath && !window.location.pathname.startsWith(tour.requiredPath)) {
      return false;
    }

    // If this is a chained tour (part of the sequence), always allow it
    if (isChainedTour) {
      return true;
    }

    const storage = getTourStorage();

    // Check if tour was completed
    if (storage.completedTours.includes(tour.id)) {
      return false;
    }

    // EXCEPTION: For first-time tour with auto-start enabled, bypass restrictions
    // The session flag will prevent showing on every refresh, and the toggle controls auto-start
    const autoStartGloballyEnabled = localStorage.getItem('oogwai_tour_auto_start') === 'true';
    const isBypassingRestrictions = tour.id === 'first-time-tour' && autoStartGloballyEnabled;

    // Check if tour was skipped and we don't want to show skipped tours
    if (!isBypassingRestrictions && !showIfSkipped && storage.skippedTours.includes(tour.id)) {
      return false;
    }

    // Check view count limit
    if (!isBypassingRestrictions) {
      const viewCount = storage.viewCount[tour.id] || 0;
      if (viewCount >= maxViewCount) {
        return false;
      }
    }

    return true;
  }, [showIfSkipped, maxViewCount]);

  // Start tour function
  const startTour = useCallback((tour: TourData, isChainedTour: boolean = false) => {
    if (!shouldShowTour(tour, isChainedTour)) {
      return;
    }

    // Mark that first-time tour has been shown in this session (prevents auto-start on refresh)
    if (tour.id === 'first-time-tour') {
      sessionStorage.setItem('oogwai_first_time_tour_shown', 'true');
    }

    // Set chain sequence flag if this is a chained tour or the first-time tour
    // First-time tour should ALWAYS chain (both auto-start and manual triggers)
    if (isChainedTour || tour.id === 'first-time-tour') {
      setIsInChainSequence(true);
    } else {
      setIsInChainSequence(false);
    }

    setCurrentTour(tour);

    // Update view count
    const storage = getTourStorage();
    storage.viewCount[tour.id] = (storage.viewCount[tour.id] || 0) + 1;
    storage.lastShown[tour.id] = new Date().toISOString();
    saveTourStorage(storage);

    // Track analytics
    trackTourEvent('started', tour.id);

    if (delay > 0) {
      setTimeout(() => {
        setRunTour(true);
      }, delay);
    } else {
      setRunTour(true);
    }
  }, [shouldShowTour, delay]);

  // Close tour (completed)
  const closeTour = useCallback(() => {
    if (!currentTour) {
      return;
    }

    const storage = getTourStorage();

    // Mark as completed
    if (!storage.completedTours.includes(currentTour.id)) {
      storage.completedTours.push(currentTour.id);
    }

    // Remove from skipped if it was there
    storage.skippedTours = storage.skippedTours.filter(id => id !== currentTour.id);

    saveTourStorage(storage);
    trackTourEvent('completed', currentTour.id);

    setRunTour(false);
    setCurrentTour(null);

    // Check if we should chain to the next tour
    // Only chain if we're in an active chain sequence (not a manual tour start)
    if (chainToNext && isInChainSequence) {
      const nextTour = getNextTour(currentTour.id);

      if (nextTour) {
        // If next tour requires a specific path, navigate there first
        // Use startsWith to allow for nested routes
        if (nextTour.requiredPath && !window.location.pathname.startsWith(nextTour.requiredPath)) {
          // Store the pending tour in sessionStorage so it can be triggered after navigation
          sessionStorage.setItem('oogwai_pending_tour', nextTour.id);

          // Use window.location for navigation (works without router context)
          window.location.href = nextTour.requiredPath;
          return;
        } else {
          // Already on correct page, start next tour after a short delay
          setTimeout(() => {
            const event = new CustomEvent('startTour', {
              detail: { ...nextTour, isChainedTour: true }
            });
            window.dispatchEvent(event);
          }, 500);
        }
      } else {
        setIsInChainSequence(false);
      }
    } else {
      setIsInChainSequence(false);
    }
  }, [currentTour, chainToNext, isInChainSequence]);

  // Skip tour
  const skipTour = useCallback(() => {
    if (!currentTour) return;

    const storage = getTourStorage();

    // Mark as skipped
    if (!storage.skippedTours.includes(currentTour.id)) {
      storage.skippedTours.push(currentTour.id);
    }

    saveTourStorage(storage);
    trackTourEvent('skipped', currentTour.id);

    setRunTour(false);
    setCurrentTour(null);

    // Reset chain sequence when tour is skipped
    setIsInChainSequence(false);
  }, [currentTour]);

  // Reset all tours (for development/testing)
  const resetTours = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRunTour(false);
    setCurrentTour(null);
  }, []);

  // Check if tour is completed
  const isTourCompleted = useCallback((tourId: string): boolean => {
    const storage = getTourStorage();
    return storage.completedTours.includes(tourId);
  }, []);

  // Check if tour is skipped
  const isTourSkipped = useCallback((tourId: string): boolean => {
    const storage = getTourStorage();
    return storage.skippedTours.includes(tourId);
  }, []);

  // Get tour statistics
  const getTourStats = useCallback((): TourStorage => {
    return getTourStorage();
  }, []);

  // Auto-start tour on mount if provided
  useEffect(() => {
    // Check for pending tour from navigation
    const pendingTourId = sessionStorage.getItem('oogwai_pending_tour');
    if (pendingTourId && tourToShow?.id === pendingTourId) {
      sessionStorage.removeItem('oogwai_pending_tour');

      // Start the pending tour after a delay to allow page to fully load
      setTimeout(() => {
        const event = new CustomEvent('startTour', {
          detail: { ...tourToShow, isChainedTour: true }
        });
        window.dispatchEvent(event);
      }, 1500);
      return;
    }

    // Check if auto-start is globally enabled (from debug helper)
    const autoStartGloballyEnabled = localStorage.getItem('oogwai_tour_auto_start') === 'true';

    // Check if another tour is already active (global coordination)
    const activeTourId = localStorage.getItem('activeTourId');
    const isTourActive = activeTourId && activeTourId !== tourToShow?.id;

    // For first-time tour auto-start, check session flag to prevent showing on every refresh
    let shouldAutoStartFirstTime = true;
    if (tourToShow?.id === 'first-time-tour' && autoStart && autoStartGloballyEnabled) {
      const hasShownInSession = sessionStorage.getItem('oogwai_first_time_tour_shown');
      shouldAutoStartFirstTime = !hasShownInSession;
    }

    if (autoStart && autoStartGloballyEnabled && tourToShow && shouldShowTour(tourToShow) && !isTourActive && shouldAutoStartFirstTime) {
      startTour(tourToShow);
    }
  }, [tourToShow, autoStart, shouldShowTour, startTour]);

  return {
    runTour,
    currentTour,
    startTour,
    closeTour,
    skipTour,
    resetTours,
    isTourCompleted,
    isTourSkipped,
    getTourStats,
  };
};

// Utility hook for manual tour triggering
export const useTourTrigger = () => {
  const startManualTour = useCallback((tour: TourData) => {
    // Clear completed/skipped status and reset view count to allow replay
    const storage = getTourStorage();
    storage.completedTours = storage.completedTours.filter(id => id !== tour.id);
    storage.skippedTours = storage.skippedTours.filter(id => id !== tour.id);
    // Reset view count to allow unlimited manual replays
    delete storage.viewCount[tour.id];
    saveTourStorage(storage);

    const event = new CustomEvent('startTour', { detail: tour });
    window.dispatchEvent(event);
  }, []);

  const replayTour = useCallback((tourId: string) => {
    // Remove completed status and reset view count to allow replay
    const storage = getTourStorage();
    storage.completedTours = storage.completedTours.filter(id => id !== tourId);
    storage.skippedTours = storage.skippedTours.filter(id => id !== tourId);
    // Reset view count to allow unlimited replays
    delete storage.viewCount[tourId];
    saveTourStorage(storage);

    // Trigger page reload to restart tour
    window.location.reload();
  }, []);

  return {
    startManualTour,
    replayTour,
  };
};

export default useOnboardingTour;